/* Optional reciprocal add-on. Shopify is authoritative for prices and eligibility. */
(function () {
  'use strict';
  const owned = (item, token) => item.properties?._mic_lock_pair === token;
  const codeNames = (cart) => Array.isArray(cart.discount_codes) && cart.discount_codes.every((d) => typeof d?.code === 'string' && d.code.trim()) ? cart.discount_codes.map((d) => d.code) : null;
  const zeroAmounts = (entries) => entries === undefined || (Array.isArray(entries) && entries.every((entry) => entry?.amount === 0));
  const fullPrice = (item) => Number.isSafeInteger(item.original_price) && item.original_price > 0 && item.final_price === item.original_price;
  const neutralLineDiscounts = (item) => zeroAmounts(item.line_level_discount_allocations) && zeroAmounts(item.discounts);
  const sameProperties = (a, b) => JSON.stringify(Object.entries(a || {}).sort()) === JSON.stringify(Object.entries(b || {}).sort());
  const unchangedQualifier = (before, after) => before && after && before.id === after.id && before.quantity === after.quantity &&
    before.original_price === after.original_price && before.final_price === after.final_price &&
    sameProperties(before.properties, after.properties) && !after.selling_plan_allocation && !after.selling_plan &&
    fullPrice(after) && neutralLineDiscounts(after);
  const retainsCodes = (before, after) => before.every((code) => after.some((current) => current.toUpperCase() === code.toUpperCase()));
  function offer(cart, products, codes) {
    if (!cart || cart.currency !== 'USD' || cart.item_count !== 1 || cart.items?.length !== 1 || cart.total_discount !== 0) return null;
    const names = codeNames(cart);
    if (!names) return null;
    if (cart.cart_level_discount_applications?.some((d) => d.target_type !== 'shipping_line')) return null;
    const item = cart.items[0];
    if (item.quantity !== 1 || item.selling_plan_allocation || item.selling_plan || !fullPrice(item) || !neutralLineDiscounts(item)) return null;
    const first = Object.values(products).find((p) => p.id === item.id);
    if (!first?.available) return null;
    const product = first.id === products.webcam.id ? products.usb : products.webcam;
    if (!product?.available || !Number.isSafeInteger(product.price) || product.price <= 0 || !codes[product.id]) return null;
    // Raw codes can be dormant or shipping-only. Actual item/order allocations govern eligibility.
    return {first, product, code: codes[product.id], price: product.price - Math.floor(product.price * 30 / 100)};
  }
  function verified(cart, selected, token) {
    if (!cart || cart.currency !== 'USD' || cart.item_count !== 2 || cart.items?.length !== 2) return false;
    const first = cart.items.find((i) => i.id === selected.first.id);
    const reward = cart.items.find((i) => i.id === selected.product.id && owned(i, token));
    if (!first || !reward || first.quantity !== 1 || reward.quantity !== 1) return false;
    if (!fullPrice(first) || !neutralLineDiscounts(first)) return false;
    if (selected.qualifier && !unchangedQualifier(selected.qualifier, first)) return false;
    const names = codeNames(cart);
    if (!names || !retainsCodes(selected.priorCodes || [], names)) return false;
    const allocations = reward.line_level_discount_allocations;
    const saving = selected.product.price - selected.price;
    return saving > 0 && reward.original_price === selected.product.price && reward.final_price === selected.price &&
      cart.total_discount === saving && cart.total_price === first.original_price + selected.price &&
      allocations?.length === 1 && allocations[0].amount === saving &&
      allocations[0].discount_application?.title === selected.code &&
      cart.discount_codes?.some((d) => d.code === selected.code && d.applicable) &&
      !cart.cart_level_discount_applications?.some((d) => d.target_type !== 'shipping_line');
  }
  function createRunner(api) {
    let busy = false;
    async function rollback(token, selected, state = {}) {
      let cart = await api.get();
      const lines = cart.items.filter((i) => owned(i, token));
      // A failed transport can finish on the server later. Absence is not proof of failure.
      if (!lines.length && !state.addConfirmed) throw Error('Add outcome still unknown');
      if (lines.length > 1 || lines.some((i) => i.quantity !== 1 || i.id !== selected.product.id)) {
        throw Error('Shopper changed add-on; manual review required');
      }
      for (const line of lines) await api.remove(line.key);
      cart = await api.get();
      if (cart.items.some((i) => owned(i, token))) throw Error('Could not remove add-on');
      // Leave coupons intact. Replacing a list can erase a code entered in another tab.
      api.clearPending?.();
      return cart;
    }
    return {
      rollback,
      async accept(products, codes, token, expected) {
        if (busy) return {ok:false, busy:true};
        busy = true;
        let selected, started = false, addConfirmed = false, couponChanged = false;
        try {
          const before = await api.get();
          selected = offer(before, products, codes);
          if (!selected || (expected && (selected.product.id !== expected.product.id || selected.price !== expected.price))) return {ok:false, stale:true};
          selected = {...selected, qualifier:before.items[0], priorCodes:codeNames(before)};
          // This record allows a later page load to recover a interrupted request.
          api.pending?.({token, selected});
          started = true;
          await api.add(selected.product.id, token);
          addConfirmed = true;
          api.pending?.({token, selected, addConfirmed:true});
          const added = await api.get();
          // Shopify can change a line key after price/discount updates; compare the unchanged qualifier itself.
          const first = added.items.find((i) => i.id === before.items[0].id && !owned(i, token));
          if (added.item_count !== 2 || added.items.length !== 2 || !first || first.quantity !== 1 ||
            !unchangedQualifier(before.items[0], first) ||
            !added.items.some((i) => owned(i, token) && i.quantity === 1) ||
            !codeNames(added) || !retainsCodes(selected.priorCodes, codeNames(added))) throw Error('Cart changed');
          selected.priorCodes = [...new Set([...selected.priorCodes, ...codeNames(added)])];
          // Re-read immediately before the native route and preserve codes entered in another tab.
          const ready = await api.get();
          const readyFirst = ready.items.find((i) => i.id === before.items[0].id && !owned(i, token));
          const readyReward = ready.items.find((i) => owned(i, token));
          if (ready.item_count !== 2 || ready.items.length !== 2 || !unchangedQualifier(before.items[0], readyFirst) ||
            !readyReward || readyReward.id !== selected.product.id || readyReward.quantity !== 1 ||
            !codeNames(ready) || !retainsCodes(selected.priorCodes, codeNames(ready)) ||
            ready.cart_level_discount_applications?.some((d) => d.target_type !== 'shipping_line')) throw Error('Cart changed');
          selected.priorCodes = [...new Set([...selected.priorCodes, ...codeNames(ready)])];
          api.pending?.({token, selected, addConfirmed:true});
          await api.discount(selected.code);
          const after = await api.get();
          couponChanged = !codeNames(after) || !retainsCodes(selected.priorCodes, codeNames(after));
          if (!verified(after, selected, token)) throw Error('Discount not confirmed');
          api.clearPending?.();
          return {ok:true, cart:after};
        } catch (_) {
          if (!started) return {ok:false};
          try { return {ok:false, couponChanged, cart:await rollback(token, selected, {addConfirmed})}; }
          catch (_) { return {ok:false, couponChanged, recoveryRequired:true}; }
        } finally { busy = false; }
      }
    };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {offer, verified, createRunner};
  if (typeof window === 'undefined' || customElements.get('mic-lock-pair-offer')) return;

  const root = window.Shopify?.routes?.root || '/';
  const pendingKey = 'mic-lock-pair-pending-v1';
  const messageKey = 'mic-lock-pair-message-v1';
  let operation = false;
  let recovery = null;
  let recoveryNavigation = false;
  async function fetchWithTimeout(url, options, readJSON = false) {
    const controller = new AbortController();
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {controller.abort(); reject(Error('Cart request timed out'));}, 12000);
    });
    try {
      return await Promise.race([fetch(url, {...options, signal:controller.signal}).then(async (response) => {
        if (!response.ok) throw Error('Cart request failed');
        return readJSON ? response.json() : response;
      }), timeout]);
    } finally {clearTimeout(timer);}
  }
  async function request(path, body) {
    const data = await fetchWithTimeout(root + path, {
      method: body ? 'POST' : 'GET', credentials:'same-origin', cache:'no-store',
      headers:{'Content-Type':'application/json','Accept':'application/json'},
      ...(body ? {body:JSON.stringify(body)} : {})
    }, true);
    if (data.status >= 400 || data.errors) throw Error('Cart request failed');
    return data;
  }
  const api = {
    get: () => request('cart.js'),
    add: (id, token) => request('cart/add.js', {items:[{id, quantity:1, properties:{_mic_lock_pair:token}}]}),
    discount: async (discount) => {
      // Shopify's shareable route adds a code using native combination rules.
      // Do not use cart/update discount, which replaces every existing code.
      await fetchWithTimeout(root + 'discount/' + encodeURIComponent(discount) + '?redirect=' + encodeURIComponent(root + 'cart'), {
        credentials:'same-origin', cache:'no-store', redirect:'follow'
      });
    },
    remove: (id) => request('cart/change.js', {id, quantity:0}),
    pending: (record) => sessionStorage.setItem(pendingKey, JSON.stringify(record)),
    clearPending: () => sessionStorage.removeItem(pendingKey)
  };
  const runner = createRunner(api);
  const outcomeMessage = (result) => result.ok ? 'Your add-on is in the cart with 30% off. Shipping and tax are calculated at checkout.' :
    result.recoveryRequired ? 'We could not confirm the add-on discount. Check the item prices below and remove the add-on if its discount is missing.' :
    result.couponChanged ? 'The offer could not be applied. No add-on was kept. Please review your existing discount codes before checkout.' :
    'The offer could not be applied. No add-on was kept. Please review your cart.';
  function lockPage() {
    const disabled = [...document.querySelectorAll('button, input, select')].filter((c) => !c.disabled);
    disabled.forEach((c) => {c.disabled = true;});
    const stop = (event) => {
      if (event.type === 'submit' || event.target.closest('a[href*="/checkout"], a[href*="/cart/change"]')) event.preventDefault();
    };
    document.addEventListener('submit', stop, true);
    document.addEventListener('click', stop, true);
    return () => {
      disabled.forEach((c) => {if(c.isConnected) c.disabled = false;});
      document.removeEventListener('submit', stop, true);
      document.removeEventListener('click', stop, true);
    };
  }
  class PairOffer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {this.config = JSON.parse(this.querySelector('[data-pair-config]').textContent);} catch (_) {return;}
      this.status = this.querySelector('[data-pair-status]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-pair-accept]')) this.accept();
        if (event.target.closest('[data-pair-dismiss]')) {
          this.dismissed = true; this.hidden = true;
        }
      });
      if (typeof subscribe === 'function') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      this.refresh();
    }
    disconnectedCallback() { this.unsubscribe?.(); }
    async refresh() {
      if ((operation && !recovery) || this.dismissed) return;
      this.hidden = true;
      const revision = this.revision = (this.revision || 0) + 1;
      try {
        const pending = JSON.parse(sessionStorage.getItem(pendingKey) || 'null');
        if (pending) {
          if (!recovery) {
            operation = true;
            recovery = (async () => {
              const unlock = lockPage();
              try {
                const cart = await api.get();
                if (!verified(cart, pending.selected, pending.token)) await runner.rollback(pending.token, pending.selected, pending);
                else api.clearPending();
                return true;
              } catch (_) { return false; }
              finally {operation = false; unlock();}
            })();
          }
          const resolved = await recovery;
          if (resolved && !recoveryNavigation) {
            recoveryNavigation = true;
            sessionStorage.setItem(messageKey, 'Your cart has been refreshed. Please review the total below.');
            location.assign(root + 'cart');
          } else if (!resolved) {
            recovery = null; // A later cart update must be able to reconcile again.
            this.selected = null;
            this.querySelectorAll('[data-pair-product]').forEach((c) => {c.hidden = true;});
            this.hidden = false;
            this.status.textContent = 'We could not confirm the add-on discount. Please check your cart and remove the add-on before checkout if its discount is missing.';
          }
          return;
        }
        const message = sessionStorage.getItem(messageKey);
        if (message && !this.closest('cart-drawer')) {
          this.status.textContent = message;
          sessionStorage.removeItem(messageKey);
          this.hidden = false;
        }
        const cart = await api.get();
        if (!this.isConnected || revision !== this.revision || operation) return;
        const selected = offer(cart, this.config.products, this.config.codes);
        this.querySelectorAll('[data-pair-product]').forEach((c) => {c.hidden = true;});
        if (!selected) return;
        this.selected = selected;
        this.querySelector(`[data-pair-product="${selected.product.id}"]`).hidden = false;
        this.hidden = false;
      } catch (_) { /* Hide optional offer on unknown state; normal cart stays usable. */ }
    }
    async accept() {
      if (operation || !this.selected || (typeof CartItems !== 'undefined' && CartItems.quantityUpdatePending)) return;
      operation = true;
      const unlock = lockPage();
      this.status.textContent = 'Adding your discounted item…';
      let result;
      try {
        // Fetch current product availability/prices before any write.
        const products = {};
        for (const [name, product] of Object.entries(this.config.products)) {
          const data = await request(`products/${product.handle}.js`);
          const variant = data.variants.find((v) => v.id === product.id);
          products[name] = {...product, price:variant?.price, available:variant?.available === true};
        }
        result = await runner.accept(products, this.config.codes, crypto.randomUUID(), this.selected);
        const message = outcomeMessage(result);
        sessionStorage.setItem(messageKey, message);
        location.assign(root + 'cart');
      } catch (_) {
        if (result) {
          this.selected = null;
          this.querySelectorAll('[data-pair-product]').forEach((panel) => {panel.hidden = true;});
        }
        this.status.textContent = result ? outcomeMessage(result) + ' Reload the cart to review the current total.' :
          'The offer is unavailable right now. Your cart has not been changed.';
      } finally {operation = false; unlock();}
    }
  }
  customElements.define('mic-lock-pair-offer', PairOffer);
}());
